import type { ExpenseItem } from "../expense-items/generators";
import {
  EXPENSE_ACTIVE_STATUS,
  EXPENSE_DOWNLOAD_FAILS,
  EXPENSE_LOWERCASE_PAYEE,
  EXPENSE_NO_PERIOD_PENDING,
  EXPENSE_OLDER_PERIOD,
  EXPENSE_ON_ACTIVE_REPORT,
  EXPENSE_ON_APPROVED_REPORT,
  EXPENSE_PAID_WITH_SESSIONS,
  EXPENSE_UPPERCASE_PAYEE,
} from "../expense-items/stubs";
import { COMPANY_NAMES, period } from "../shared";

import { generateMockExpenseReport } from "./generators";
import type { ExpenseReportSummary, InvalidInformation } from "./generators";

const INVALID_INFORMATION_VARIANTS = {
  kycNotPassed: { kycNotPassed: true },
  missingReimbursementRates: {
    missingReimbursementRates: [
      {
        locationId: 4021,
        locationName: "Aarhus Nord",
        cdrEndDates: ["2026-05-14T08:12:00.000Z"],
      },
    ],
  },
  recipientList: {
    recipientList: [
      {
        customerCrmId: "CRM-9002",
        name: "Rasmus Holm",
        invalidFields: ["IBAN", "address"],
      },
    ],
  },
} as const satisfies Record<string, InvalidInformation>;

const PENDING_CLEAN = generateMockExpenseReport({
  id: 19,
  companyName: COMPANY_NAMES.pendingClean,
  period: period(2026, 6),
  status: "Pending",
  totalAmountInCents: 2_140_00,
  totalConsumptionInWattHours: 1_355_000,
  emailAddressList: ["ap@kbhlogistik.example"],
});

const PENDING_INVALID = generateMockExpenseReport({
  id: 20,
  companyName: "Nordic Fleet A/S",
  period: period(2026, 6),
  status: "Pending",
  totalAmountInCents: 1_284_50,
  totalConsumptionInWattHours: 842_000,
  invalidInformation: INVALID_INFORMATION_VARIANTS.recipientList,
  emailAddressList: ["finance@nordicfleet.example"],
});

const APPROVED = generateMockExpenseReport({
  id: 21,
  companyName: COMPANY_NAMES.approved,
  period: period(2026, 5),
  status: "Approved",
  totalAmountInCents: 964_25,
  totalConsumptionInWattHours: 612_500,
  emailAddressList: ["payables@vestjysk.example"],
});

const FUNDS_RECEIVED = generateMockExpenseReport({
  id: 22,
  companyName: "Sjaelland Bus A/S",
  period: period(2026, 4),
  status: "FundsReceived",
  totalAmountInCents: 1_776_40,
  totalConsumptionInWattHours: 1_102_300,
});

const ACTIVE_CLEAN = generateMockExpenseReport({
  id: 23,
  companyName: COMPANY_NAMES.activeClean,
  period: period(2026, 7),
  status: "Active",
  totalAmountInCents: 312_75,
  totalConsumptionInWattHours: 198_400,
});

const ACTIVE_INVALID = generateMockExpenseReport({
  id: 24,
  companyName: "Jylland Distribution ApS",
  period: period(2026, 7),
  status: "Active",
  totalAmountInCents: 88_10,
  totalConsumptionInWattHours: 54_900,
  invalidInformation: INVALID_INFORMATION_VARIANTS.kycNotPassed,
});

const KYC_NOT_PASSED = generateMockExpenseReport({
  id: 25,
  companyName: "Aalborg Taxi A/S",
  period: period(2026, 5),
  status: "Pending",
  totalAmountInCents: 445_00,
  totalConsumptionInWattHours: 287_100,
  invalidInformation: INVALID_INFORMATION_VARIANTS.kycNotPassed,
});

const AWAITING_EARLIER_REPORTS = generateMockExpenseReport({
  id: 26,
  companyName: "Odense Varevogne ApS",
  period: period(2026, 5),
  status: "Pending",
  totalAmountInCents: 733_90,
  totalConsumptionInWattHours: 466_200,
  invalidInformation: {
    awaitingExpenseReportList: [PENDING_CLEAN.id, PENDING_INVALID.id],
  },
});

const MISSING_RATES = generateMockExpenseReport({
  id: 27,
  companyName: "Esbjerg Havnetransport A/S",
  period: period(2026, 4),
  status: "Pending",
  totalAmountInCents: 1_050_00,
  totalConsumptionInWattHours: 671_800,
  invalidInformation: INVALID_INFORMATION_VARIANTS.missingReimbursementRates,
});

const INVALID_BENEFICIARY = generateMockExpenseReport({
  id: 28,
  companyName: "Randers Kurer ApS",
  period: period(2026, 4),
  status: "Pending",
  totalAmountInCents: 289_45,
  totalConsumptionInWattHours: 176_300,
  invalidInformation: {
    invalidBeneficiary: {
      customerCrmId: "CRM-7781",
      expenseIds: [EXPENSE_PAID_WITH_SESSIONS.id],
    },
  },
});

const DOWNLOAD_FAILS = generateMockExpenseReport({
  id: 29,
  companyName: "Silkeborg Anlaeg A/S",
  period: period(2026, 4),
  status: "Approved",
  totalAmountInCents: 620_00,
  totalConsumptionInWattHours: 401_200,
});

const CURRENCY_ABSENT = generateMockExpenseReport({
  id: 30,
  companyName: "Horsens Servicebiler ApS",
  period: period(2026, 3),
  status: "Approved",
  currency: undefined,
  totalAmountInCents: 512_30,
  totalConsumptionInWattHours: 330_700,
});

const CURRENCY_NULL = generateMockExpenseReport({
  id: 31,
  companyName: "Vejle Entreprenor A/S",
  period: period(2026, 3),
  status: "Approved",
  currency: null,
  totalAmountInCents: 1_399_99,
  totalConsumptionInWattHours: 884_600,
});

const FIRST_FILLER_REPORT_ID = 32;

const FILLER_REPORTS = Array.from({ length: 17 }, (_, index) =>
  generateMockExpenseReport({
    id: FIRST_FILLER_REPORT_ID + index,
    companyName: `Filler Fleet ${String(index + 1).padStart(3, "0")} A/S`,
    period: period(2026, (index % 12) + 1),
    status:
      index % 3 === 0
        ? "Pending"
        : index % 3 === 1
          ? "Approved"
          : "FundsReceived",
    totalAmountInCents: 10_000 + index * 1_137,
    totalConsumptionInWattHours: 40_000 + index * 3_301,
  })
);

const SEEDED_REPORTS: ExpenseReportSummary[] = [
  PENDING_INVALID,
  APPROVED,
  PENDING_CLEAN,
  ACTIVE_CLEAN,
  FUNDS_RECEIVED,
  ACTIVE_INVALID,
  KYC_NOT_PASSED,
  AWAITING_EARLIER_REPORTS,
  MISSING_RATES,
  INVALID_BENEFICIARY,
  DOWNLOAD_FAILS,
  CURRENCY_ABSENT,
  CURRENCY_NULL,
  ...FILLER_REPORTS,
];

const UNREADABLE_REPORT_ROW = {
  id: "not-a-number",
  companyName: "Corrupt Row ApS",
  period: { year: 2026, month: 6 },
  status: "Pending",
  currency: "DKK",
  totalAmountInCents: 1_00,
  totalConsumptionInWattHours: 1_000,
};

const SEEDED_EXPENSES_BY_REPORT: Record<number, ExpenseItem[]> = {
  [PENDING_CLEAN.id]: [
    EXPENSE_UPPERCASE_PAYEE,
    EXPENSE_LOWERCASE_PAYEE,
    EXPENSE_DOWNLOAD_FAILS,
    EXPENSE_PAID_WITH_SESSIONS,
    EXPENSE_ACTIVE_STATUS,
    EXPENSE_OLDER_PERIOD,
    EXPENSE_NO_PERIOD_PENDING,
  ],
  [APPROVED.id]: [EXPENSE_ON_APPROVED_REPORT],
  [ACTIVE_CLEAN.id]: [EXPENSE_ON_ACTIVE_REPORT],
};

export {
  ACTIVE_CLEAN,
  ACTIVE_INVALID,
  APPROVED,
  AWAITING_EARLIER_REPORTS,
  CURRENCY_ABSENT,
  CURRENCY_NULL,
  DOWNLOAD_FAILS,
  FUNDS_RECEIVED,
  INVALID_BENEFICIARY,
  KYC_NOT_PASSED,
  MISSING_RATES,
  PENDING_CLEAN,
  PENDING_INVALID,
  SEEDED_EXPENSES_BY_REPORT,
  SEEDED_REPORTS,
  UNREADABLE_REPORT_ROW,
};
